import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import OsScroll from './ui/OsScroll';

// A fully custom (no native <input type="date">, no OS chrome) date / datetime
// / time picker popover for the DB Manager grid. It's a controlled overlay:
// the parent decides when it's open and where it's anchored, and gets the
// chosen value back as a plain string in the canonical SQL shape for its kind:
//   date     -> YYYY-MM-DD
//   datetime -> YYYY-MM-DD HH:MM:SS
//   time     -> HH:MM:SS
// Layout: calendar on the left, a scrollable time picker on the right (only
// for kinds that carry a time), and an editable text field underneath that
// both reflects the current selection and accepts a value typed by hand.

const WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const HOURS = Array.from({ length: 24 }, (_, i) => i);
const MINSEC = Array.from({ length: 60 }, (_, i) => i);

function pad(n) {
  return String(n).padStart(2, '0');
}

// Pull whatever date/time fields the string carries; anything missing falls
// back to today (for the date half) and midnight (for the time half), so the
// picker always opens on a sensible month even for an empty/NULL cell.
function parseParts(value) {
  const now = new Date();
  let y = now.getFullYear();
  let mo = now.getMonth() + 1;
  let d = now.getDate();
  let h = 0;
  let mi = 0;
  let s = 0;
  let hasDate = false;
  const str = value == null ? '' : String(value);
  const dm = str.match(/(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (dm) {
    y = Number(dm[1]);
    mo = Number(dm[2]);
    d = Number(dm[3]);
    hasDate = true;
  }
  const tm = str.match(/(\d{1,2}):(\d{2})(?::(\d{2}))?/);
  if (tm) {
    h = Number(tm[1]);
    mi = Number(tm[2]);
    s = tm[3] ? Number(tm[3]) : 0;
  }
  return { y, mo, d, h, mi, s, hasDate };
}

function formatParts(parts, kind) {
  const date = `${parts.y}-${pad(parts.mo)}-${pad(parts.d)}`;
  const time = `${pad(parts.h)}:${pad(parts.mi)}:${pad(parts.s)}`;
  if (kind === 'date') return date;
  if (kind === 'time') return time;
  return `${date} ${time}`;
}

function daysInMonth(year, month /* 1-12 */) {
  return new Date(year, month, 0).getDate();
}

// One scrollable column of the time picker (hours / minutes / seconds),
// scrolled with the project's OverlayScrollbars (OsScroll) rather than the
// native scrollbar. The selected value is centered on change by setting
// scrollTop on OverlayScrollbars' internal viewport element; a rAF retry
// covers OsScroll's deferred initialization on first mount.
function TimeColumn({ label, values, value, onSelect }) {
  const osRef = useRef(null);
  const selRef = useRef(null);
  useEffect(() => {
    let raf;
    const center = () => {
      const vp = osRef.current?.osInstance?.()?.elements?.().viewport;
      const el = selRef.current;
      if (!vp || !el) { raf = requestAnimationFrame(center); return; }
      const vpRect = vp.getBoundingClientRect();
      const elRect = el.getBoundingClientRect();
      vp.scrollTop += (elRect.top - vpRect.top) - (vp.clientHeight / 2 - el.clientHeight / 2);
    };
    center();
    return () => raf && cancelAnimationFrame(raf);
  }, [value]);
  return (
    <div className="dbm-dp-tc">
      <span className="dbm-dp-tc-label">{label}</span>
      <OsScroll ref={osRef} className="dbm-dp-tc-list">
        <div className="dbm-dp-tc-inner">
          {values.map((n) => (
            <button
              key={n}
              ref={n === value ? selRef : null}
              type="button"
              className={`dbm-dp-tc-item${n === value ? ' dbm-dp-tc-item--sel' : ''}`}
              onClick={() => onSelect(n)}
            >
              {pad(n)}
            </button>
          ))}
        </div>
      </OsScroll>
    </div>
  );
}

export default function DbDatePicker({ kind, value, anchorRect, onPick, onClear, onClose }) {
  const initial = useMemo(() => parseParts(value), [value]);
  const [parts, setParts] = useState(initial);
  const [text, setText] = useState(() => (value == null ? '' : String(value)));
  // The visible month can be paged independent of the selected day.
  const [view, setView] = useState({ y: initial.y, mo: initial.mo });
  const rootRef = useRef(null);

  // Re-seed when reopened on a different cell (anchorRect identity changes).
  useEffect(() => {
    const next = parseParts(value);
    setParts(next);
    setView({ y: next.y, mo: next.mo });
    setText(value == null ? '' : String(value));
  }, [value, anchorRect]);

  // Dismiss on outside click / Escape / scroll, mirroring the grid's context
  // menu. Capture-phase keydown so Escape closes the picker before the grid's
  // own Escape handling (discard edits / clear selection) can fire.
  useEffect(() => {
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) onClose();
    };
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }
    };
    const onScroll = (e) => {
      // Ignore scrolls that happen inside the picker itself (the time columns).
      if (rootRef.current && rootRef.current.contains(e.target)) return;
      onClose();
    };
    window.addEventListener('mousedown', onDown, true);
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      window.removeEventListener('mousedown', onDown, true);
      window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [onClose]);

  const showCalendar = kind !== 'time';
  const showTime = kind !== 'date';

  const grid = useMemo(() => {
    if (!showCalendar) return [];
    const first = new Date(view.y, view.mo - 1, 1).getDay();
    const total = daysInMonth(view.y, view.mo);
    const cells = [];
    for (let i = 0; i < first; i++) cells.push(null);
    for (let day = 1; day <= total; day++) cells.push(day);
    return cells;
  }, [view, showCalendar]);

  const today = useMemo(() => {
    const n = new Date();
    return { y: n.getFullYear(), mo: n.getMonth() + 1, d: n.getDate() };
  }, []);

  const syncText = (next) => setText(formatParts(next, kind));

  const stepMonth = (delta) => {
    setView((v) => {
      const idx = v.y * 12 + (v.mo - 1) + delta;
      return { y: Math.floor(idx / 12), mo: (idx % 12) + 1 };
    });
  };

  const pickDay = (day) => {
    const next = { ...parts, y: view.y, mo: view.mo, d: day, hasDate: true };
    setParts(next);
    syncText(next);
    // A pure date has nothing left to choose — apply immediately. Datetime
    // keeps the popover open so the time can still be dialed in.
    if (kind === 'date') onPick(formatParts(next, kind));
  };

  const setTimeUnit = (patch) => {
    const next = { ...parts, ...patch, hasDate: parts.hasDate || showCalendar };
    setParts(next);
    syncText(next);
  };

  const setNow = () => {
    const n = new Date();
    const next = {
      y: n.getFullYear(), mo: n.getMonth() + 1, d: n.getDate(),
      h: n.getHours(), mi: n.getMinutes(), s: n.getSeconds(), hasDate: true,
    };
    setParts(next);
    setView({ y: next.y, mo: next.mo });
    syncText(next);
    if (kind === 'date') onPick(formatParts(next, kind));
  };

  // Typed input is the source of truth while the user edits it: parse out
  // whatever date/time fields are complete so the calendar and time columns
  // track the typing, but never fight the raw text they're entering.
  const onTextChange = (v) => {
    setText(v);
    const dm = v.match(/(\d{4})-(\d{1,2})-(\d{1,2})/);
    const tm = v.match(/(\d{1,2}):(\d{2})(?::(\d{2}))?/);
    if (!dm && !tm) return;
    setParts((prev) => {
      let next = { ...prev };
      if (dm) next = { ...next, y: +dm[1], mo: +dm[2], d: +dm[3], hasDate: true };
      if (tm) next = { ...next, h: +tm[1], mi: +tm[2], s: tm[3] ? +tm[3] : 0 };
      return next;
    });
    if (dm) setView({ y: +dm[1], mo: +dm[2] });
  };

  // Commit whatever's in the field (respecting a hand-typed value), falling
  // back to the canonical form of the current selection if it's empty.
  const commit = () => onPick(text.trim() || formatParts(parts, kind));

  // Anchor below the cell, flipping above / clamping when it would overflow.
  const width = showTime ? (showCalendar ? 402 : 190) : 250;
  const estHeight = (showCalendar ? 268 : 250) + 44;
  let left = anchorRect.left;
  let top = anchorRect.bottom + 4;
  if (left + width > window.innerWidth - 8) left = window.innerWidth - width - 8;
  if (left < 8) left = 8;
  if (top + estHeight > window.innerHeight - 8) {
    top = Math.max(8, anchorRect.top - estHeight - 4);
  }

  return ReactDOM.createPortal(
    <div
      ref={rootRef}
      className="dbm-dp"
      style={{ left, top, width }}
      role="dialog"
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div className="dbm-dp-body">
        {showCalendar && (
          <div className="dbm-dp-cal">
            <div className="dbm-dp-head">
              <button type="button" className="dbm-dp-nav" title="Previous month" onClick={() => stepMonth(-1)}>
                <ChevronLeft size={16} />
              </button>
              <span className="dbm-dp-title">{MONTHS[view.mo - 1]} {view.y}</span>
              <button type="button" className="dbm-dp-nav" title="Next month" onClick={() => stepMonth(1)}>
                <ChevronRight size={16} />
              </button>
            </div>
            <div className="dbm-dp-weekdays">
              {WEEKDAYS.map((w, i) => <span key={i}>{w}</span>)}
            </div>
            <div className="dbm-dp-days">
              {grid.map((day, i) => {
                if (day === null) return <span key={i} className="dbm-dp-day dbm-dp-day--empty" />;
                const isSel = parts.hasDate && parts.y === view.y && parts.mo === view.mo && parts.d === day;
                const isToday = today.y === view.y && today.mo === view.mo && today.d === day;
                return (
                  <button
                    key={i}
                    type="button"
                    className={`dbm-dp-day${isSel ? ' dbm-dp-day--selected' : ''}${isToday ? ' dbm-dp-day--today' : ''}`}
                    onClick={() => pickDay(day)}
                  >
                    {day}
                  </button>
                );
              })}
            </div>
          </div>
        )}
        {showTime && (
          <div className="dbm-dp-timecols">
            <TimeColumn label="Hr" values={HOURS} value={parts.h} onSelect={(h) => setTimeUnit({ h })} />
            <TimeColumn label="Min" values={MINSEC} value={parts.mi} onSelect={(mi) => setTimeUnit({ mi })} />
            <TimeColumn label="Sec" values={MINSEC} value={parts.s} onSelect={(s) => setTimeUnit({ s })} />
          </div>
        )}
      </div>
      <input
        className="dbm-dp-input"
        value={text}
        placeholder={kind === 'time' ? 'HH:MM:SS' : kind === 'date' ? 'YYYY-MM-DD' : 'YYYY-MM-DD HH:MM:SS'}
        spellCheck={false}
        onChange={(e) => onTextChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); commit(); }
        }}
      />
      <div className="dbm-dp-foot">
        <button type="button" className="dbm-dp-btn dbm-dp-btn--null" onClick={onClear}>
          Set NULL
        </button>
        <button type="button" className="dbm-dp-btn" onClick={setNow}>
          {kind === 'time' ? 'Now' : 'Today'}
        </button>
        <button type="button" className="dbm-dp-btn dbm-dp-btn--primary" onClick={commit}>
          Apply
        </button>
      </div>
    </div>,
    document.body
  );
}
